/* The people a client's content is attributed to.

   Lives beside the Vault because that is where the material they produce ends
   up, and it is admin-only for the same reason: these records decide whose name
   goes on a published page, and a wrong one is worse than none.

   The form is deliberately spare. Every field here is meant to be VERIFIABLE —
   a licence number, a real count of years, what the person actually does — and
   the help text says so, because Google's guidance treats an invented author
   as deception rather than as a shortcut. That is also why the photo is an
   UPLOAD and not a URL box: it lands as one of the client's own gallery assets,
   which is the only thing the API accepts as someone's face. */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { processImageSized } from '@/lib/image';
import { AssetImg } from '@/components/studio/AssetImg';
import { RichTextEditor } from '@/components/RichTextEditor';
import {
  getExperts, createExpert, updateExpert, archiveExpert, uploadExpertPhoto, pushExpertPage, getGithubConfig,
  exportExperts, getExpertsHook, setExpertsHook, fireExpertsHook, setExpertState, getExpertLogins,
  type Expert, type ExpertSocial, type ExpertSocialPlatform as SocialPlatform,
} from '@/data/client';

/* Mirrors SOCIAL_PLATFORMS in worker/experts.ts, which is what actually checks
   that each link belongs to the network it is filed under. */
const SOCIALS: { value: SocialPlatform; label: string; icon: string; placeholder: string }[] = [
  { value: 'linkedin', label: 'LinkedIn', icon: 'linkedin', placeholder: 'https://www.linkedin.com/in/…' },
  { value: 'instagram', label: 'Instagram', icon: 'instagram', placeholder: 'https://www.instagram.com/…' },
  { value: 'x', label: 'X', icon: 'xlogo', placeholder: 'https://x.com/…' },
  { value: 'facebook', label: 'Facebook', icon: 'facebook', placeholder: 'https://www.facebook.com/…' },
  { value: 'youtube', label: 'YouTube', icon: 'youtube', placeholder: 'https://www.youtube.com/@…' },
];
const socialMeta = (p: SocialPlatform) => SOCIALS.find((s) => s.value === p) ?? SOCIALS[0];

const BLANK = {
  name: '', title: '', credentials: [] as string[], years: null as number | null, does: '', bio: '',
  photoAsset: '', photoUrl: '', socials: [] as ExpertSocial[], clerkUserId: '',
};
type Draft = typeof BLANK;

const toDraft = (e: Expert): Draft => ({
  name: e.name, title: e.title, credentials: e.credentials, years: e.years, does: e.does, bio: e.bio,
  photoAsset: e.photoAsset, photoUrl: e.photoUrl, socials: e.socials, clerkUserId: e.clerkUserId,
});

// photoUrl is display-only; the API takes the asset id.
const toBody = ({ photoUrl: _url, ...d }: Draft) => d;

/** "API 400: {"error":"…"}" → the message inside it. */
function errorText(e: unknown): string {
  const msg = e instanceof Error ? e.message : 'Failed';
  const m = /^API \d+: ([\s\S]*)$/.exec(msg);
  if (!m) return msg;
  try { return (JSON.parse(m[1]) as { error?: string }).error || msg; } catch { return msg; }
}

const photoPath = (assetId: string) => `/api/experts/photo/${encodeURIComponent(assetId)}`;

/** The 409 the push route sends when a hand-built page is already at the path. */
function existsConflict(e: unknown): { message: string } | null {
  const msg = e instanceof Error ? e.message : '';
  const m = /^API 409: ([\s\S]*)$/.exec(msg);
  if (!m) return null;
  try {
    const j = JSON.parse(m[1]) as { error?: string; message?: string };
    return j.error === 'exists' ? { message: j.message || 'A page already exists there.' } : null;
  } catch { return null; }
}

const pushedOn = (iso: string) => {
  const d = new Date(`${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

function PhotoField({ value, onChange, name }: {
  value: { photoAsset: string; photoUrl: string };
  onChange: (v: { photoAsset: string; photoUrl: string }) => void;
  name: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      // A headshot never needs more than this; it is shown small everywhere.
      const img = await processImageSized(file, { maxDim: 1200 });
      const r = await uploadExpertPhoto(img);
      onChange({ photoAsset: r.assetId, photoUrl: r.url });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="tr-field">
      <span className="tr-field__label">Photo</span>
      <div className="expert-photo">
        <div className="expert-photo__frame">
          {value.photoAsset
            ? <AssetImg className="expert-photo__img" url={value.photoUrl} assetId={value.photoAsset} fallbackPath={photoPath(value.photoAsset)} alt={name || 'Photo'} />
            : <Icon name="users" size={22} />}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button variant="outline" size="sm" iconLeft={<Icon name="upload" size={14} />} disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Uploading…' : value.photoAsset ? 'Replace photo' : 'Upload photo'}
            </Button>
            {value.photoAsset && !busy && (
              <Button variant="ghost" size="sm" onClick={() => onChange({ photoAsset: '', photoUrl: '' })}>Remove</Button>
            )}
          </div>
          <span className="tr-field__hint">A real photo of this person — JPEG, PNG or WebP.</span>
          {err && <span className="expert-err">{err}</span>}
        </div>
        <input
          ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic" hidden
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

/* One input per credential. Each is its own claim — a licence number, a
   certification, an award — so each can be checked, and shown, on its own. */
function CredentialsField({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  // An empty list still shows one box, so the field reads as a field.
  const rows = value.length ? value : [''];
  const set = (i: number, v: string) => onChange(rows.map((c, j) => (j === i ? v : c)));
  return (
    <div className="tr-field">
      <span className="tr-field__label">Credentials</span>
      {rows.map((c, i) => (
        <div key={i} className="expert-social">
          <div className="tr-field__control expert-social__url">
            <input
              className="tr-input" aria-label={`Credential ${i + 1}`} value={c}
              placeholder={i === 0 ? 'Licensed master electrician, MN #EA12345' : 'Another licence, certification or award'}
              onChange={(e) => set(i, e.target.value)}
            />
          </div>
          {rows.length > 1 && (
            <Button variant="ghost" size="sm" aria-label="Remove" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              <Icon name="x" size={14} />
            </Button>
          )}
        </div>
      ))}
      <div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => onChange([...rows, ''])}>Add credential</Button>
      </div>
    </div>
  );
}

function SocialsField({ value, onChange }: { value: ExpertSocial[]; onChange: (v: ExpertSocial[]) => void }) {
  const set = (i: number, patch: Partial<ExpertSocial>) =>
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const add = () => {
    const used = new Set(value.map((s) => s.platform));
    const next = SOCIALS.find((s) => !used.has(s.value))?.value ?? 'linkedin';
    onChange([...value, { platform: next, url: '' }]);
  };

  return (
    <div className="tr-field">
      <span className="tr-field__label">Social profiles</span>
      {value.map((s, i) => (
        <div key={i} className="expert-social">
          <span className="expert-social__icon"><Icon name={socialMeta(s.platform).icon} size={16} /></span>
          <div className="tr-field__control expert-social__platform">
            <Select
              aria-label="Network" value={s.platform}
              options={SOCIALS.map(({ value: v, label }) => ({ value: v, label }))}
              onChange={(e) => set(i, { platform: e.target.value as SocialPlatform })}
            />
          </div>
          <div className="tr-field__control expert-social__url">
            <input
              className="tr-input" aria-label={`${socialMeta(s.platform).label} URL`}
              placeholder={socialMeta(s.platform).placeholder} value={s.url}
              onChange={(e) => set(i, { url: e.target.value })}
            />
          </div>
          <Button variant="ghost" size="sm" aria-label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Icon name="x" size={14} />
          </Button>
        </div>
      ))}
      <div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={add}>Add social link</Button>
      </div>
    </div>
  );
}

/* Linking a profile to the person's tritonIQ login.

   This is what lets a client's own FAQ answers be credited automatically,
   with nobody having to remember. Triton staff are filtered out server-side —
   they are not the client's experts, and offering them invites exactly the
   misattribution the link exists to prevent.

   A login already attached to someone else is shown and disabled rather than
   hidden, so the reason it cannot be picked is visible instead of mysterious. */
function LoginField({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data } = useQuery({ queryKey: ['expertLogins'], queryFn: getExpertLogins });
  const logins = data?.logins ?? [];
  if (data?.error) {
    return <p className="dash-muted" style={{ fontSize: 12 }}>Couldn't load this client's logins just now — the rest of the profile still saves.</p>;
  }
  if (!logins.length) return null;

  return (
    <div>
      <Select
        label="tritonIQ login (optional)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: '', label: 'Not linked' },
          ...logins.map((l) => ({
            value: l.userId,
            label: l.linkedTo && l.userId !== value
              ? `${l.name} — already linked to ${l.linkedTo}`
              : `${l.name}${l.email ? ` (${l.email})` : ''}`,
            disabled: !!l.linkedTo && l.userId !== value,
          })),
        ]}
      />
      <p className="dash-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
        Link this and their own FAQ answers are credited to them automatically.
      </p>
    </div>
  );
}

function ExpertForm({ value, onChange, onSave, onCancel, busy, saveLabel }: {
  value: Draft; onChange: (d: Draft) => void; onSave: () => void; onCancel: () => void;
  busy: boolean; saveLabel: string;
}) {
  const set = (k: 'name' | 'title' | 'years' | 'does', v: string) =>
    onChange({ ...value, [k]: k === 'years' ? (v === '' ? null : Number(v)) : v });

  return (
    <div style={{ display: 'grid', gap: 10, padding: '12px 0' }}>
      <PhotoField name={value.name} value={value} onChange={(p) => onChange({ ...value, ...p })} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        <Input label="Name" required placeholder="John Powers" value={value.name} onChange={(e) => set('name', e.target.value)} />
        <Input label="Title" placeholder="Owner · Master Electrician" value={value.title} onChange={(e) => set('title', e.target.value)} />
        <Input
          label="Years in the trade" type="number" placeholder="22"
          value={value.years ?? ''} onChange={(e) => set('years', e.target.value)}
        />
      </div>
      <CredentialsField value={value.credentials} onChange={(credentials) => onChange({ ...value, credentials })} />
      <LoginField value={value.clerkUserId} onChange={(clerkUserId) => onChange({ ...value, clerkUserId })} />
      <Textarea
        label="What they actually do"
        rows={2}
        placeholder="Runs every roof replacement crew; does the initial inspection on commercial jobs."
        value={value.does}
        onChange={(e) => set('does', e.target.value)}
      />
      <RichTextEditor
        label="Bio"
        placeholder="For their author page. Their own history, in plain terms."
        value={value.bio}
        onChange={(bio) => onChange({ ...value, bio })}
      />
      <SocialsField value={value.socials} onChange={(socials) => onChange({ ...value, socials })} />
      <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>
        <Icon name="info" size={12} /> Real people only, with details that could be checked — a licence
        number, a real count of years. An invented author is treated as deception, not as a shortcut.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" size="sm" onClick={onSave} disabled={busy || !value.name.trim()}>
          {busy ? 'Saving…' : saveLabel}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}

export function Experts() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['experts'], queryFn: getExperts, enabled: isAdmin });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [err, setErr] = useState('');

  const done = () => {
    qc.invalidateQueries({ queryKey: ['experts'] });
    setAdding(false); setEditing(null); setDraft(BLANK); setErr('');
  };
  const fail = (e: unknown) => setErr(errorText(e));

  const create = useMutation({ mutationFn: () => createExpert(toBody(draft)), onSuccess: done, onError: fail });
  const update = useMutation({ mutationFn: () => updateExpert(editing!, toBody(draft)), onSuccess: done, onError: fail });
  const archive = useMutation({ mutationFn: (id: string) => archiveExpert(id), onSuccess: done, onError: fail });

  /* Author pages go to the Astro repo whenever one is set, even for a client
     still delivering articles to DNN — see the push route in worker/experts.ts. */
  const { data: gh } = useQuery({ queryKey: ['githubConfig'], queryFn: getGithubConfig, enabled: isAdmin });
  const canPush = !!(gh?.repo && gh?.installationId);
  const [confirm, setConfirm] = useState<{ id: string; message: string } | null>(null);
  const [pushed, setPushed] = useState<{ id: string; route: string } | null>(null);
  const push = useMutation({
    mutationFn: ({ id, replace }: { id: string; replace: boolean }) => pushExpertPage(id, replace),
    onMutate: () => { setErr(''); setPushed(null); },
    onSuccess: (r, v) => {
      setConfirm(null); setPushed({ id: v.id, route: r.route });
      qc.invalidateQueries({ queryKey: ['experts'] });
    },
    onError: (e, v) => {
      const conflict = existsConflict(e);
      if (conflict) setConfirm({ id: v.id, message: conflict.message });
      else fail(e);
    },
  });
  /* The team as a file for a DNN site's TritonIQ Experts module (studio → Import
     from TritonIQ). A download the admin carries across — see worker/expert-export.ts. */
  const [exporting, setExporting] = useState(false);
  const exportJson = async () => {
    setExporting(true); setErr('');
    try {
      const doc = await exportExperts();
      const url = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `experts-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { fail(e); }
    finally { setExporting(false); }
  };
  /* The DNN site's sync URL (worker/expertshook.ts): every save here POSTs it, and
     the site's TritonIQ Experts module pulls the team. Sync now is the same call. */
  const { data: hook } = useQuery({ queryKey: ['expertsHook'], queryFn: getExpertsHook, enabled: isAdmin });
  const [hookOpen, setHookOpen] = useState(false);
  const [hookUrl, setHookUrl] = useState<string | null>(null);
  const saveHook = useMutation({
    mutationFn: () => setExpertsHook((hookUrl ?? hook?.url ?? '').trim()),
    onMutate: () => setErr(''),
    onSuccess: (h) => { qc.setQueryData(['expertsHook'], h); setHookUrl(null); },
    onError: fail,
  });
  const syncNow = useMutation({
    mutationFn: fireExpertsHook,
    onMutate: () => setErr(''),
    onSuccess: (h) => qc.setQueryData(['expertsHook'], h),
    onError: fail,
  });
  const stateM = useMutation({
    mutationFn: ({ id, state }: { id: string; state: Expert['state'] }) => setExpertState(id, state),
    onSuccess: done, onError: fail,
  });
  const busy = create.isPending || update.isPending || archive.isPending || push.isPending || stateM.isPending;

  if (!isAdmin) return null;
  const experts = data?.experts ?? [];

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="users" size={16} /> Experts</h2>
          <p>Who this client's content is attributed to — interviews, quotes and page bylines.</p>
        </div>
        {!adding && !editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              variant="outline" size="sm" iconLeft={<Icon name="download" size={14} />}
              disabled={exporting || (data?.experts.length ?? 0) === 0}
              title="Download the team as JSON for a DNN site's TritonIQ Experts module. Sites with the TritonIQ gallery token can use Sync from TritonIQ there instead."
              onClick={exportJson}
            >
              {exporting ? 'Exporting…' : 'Export JSON'}
            </Button>
            <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={15} />} onClick={() => { setDraft(BLANK); setAdding(true); }}>
              Add person
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        {err && <p className="expert-err">{err}</p>}

        <div className="gen-linkmap" style={{ marginBottom: 8 }}>
          <button type="button" className="gen-linkmap__toggle" onClick={() => setHookOpen((v) => !v)}>
            <Icon name={hookOpen ? 'chevron-down' : 'chevron-right'} size={14} /> DNN site sync
            <Badge variant={!hook?.url ? 'neutral' : hook.status && hook.status !== 'ok' ? 'warning' : 'success'}>
              {!hook?.url ? 'Not set' : hook.status && hook.status !== 'ok' ? 'Last sync failed' : 'On'}
            </Badge>
          </button>
          {hookOpen && (
            <div className="gen-linkmap__body">
              <p className="vault-add__hint">
                For a DNN site running <strong>TritonIQ Experts</strong>. Paste the sync URL from the site's experts studio
                (Import from TritonIQ → Automatic sync). Every change here then tells the site, and it pulls the team on its own.
                The site reads with the client's gallery token, so set that up there too.
              </p>
              <div className="vault-add__row">
                <Input placeholder="https://client.com/API/TritonIQExperts/Webhook/Sync?secret=…" value={hookUrl ?? hook?.url ?? ''}
                  onChange={(e) => setHookUrl(e.target.value)} />
              </div>
              <div className="vault-add__actions">
                <Button variant="primary" size="sm" disabled={saveHook.isPending || hookUrl === null} onClick={() => saveHook.mutate()}>
                  {saveHook.isPending ? 'Saving…' : 'Save sync URL'}
                </Button>
                <Button variant="outline" size="sm" disabled={syncNow.isPending || !hook?.url} onClick={() => syncNow.mutate()}>
                  {syncNow.isPending ? 'Syncing…' : 'Sync now'}
                </Button>
              </div>
              {hook?.firedAt && (
                <p className="vault-add__hint" style={{ marginTop: 6 }}>
                  Last sync {pushedOn(hook.firedAt)}: {hook.status === 'ok' ? 'the site took it.' : hook.status}
                </p>
              )}
            </div>
          )}
        </div>

        {adding && (
          <ExpertForm
            value={draft} onChange={setDraft} busy={busy} saveLabel="Add person"
            onSave={() => create.mutate()} onCancel={() => { setAdding(false); setErr(''); }}
          />
        )}

        {isLoading && <p className="dash-muted">Loading…</p>}

        {!isLoading && experts.length === 0 && !adding && (
          <p className="dash-muted" style={{ fontSize: 13, margin: '8px 0' }}>
            Nobody yet. Until someone is here, an interview records what was said but not who said it —
            and a quote cannot be attributed or a page signed.
          </p>
        )}

        {experts.map((e) => (
          <div key={e.id} style={{ borderTop: '1px solid var(--border, #e5e7eb)', padding: '10px 0' }}>
            {editing === e.id ? (
              <ExpertForm
                value={draft} onChange={setDraft} busy={busy} saveLabel="Save"
                onSave={() => update.mutate()} onCancel={() => { setEditing(null); setErr(''); }}
              />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div className="expert-photo__frame expert-photo__frame--sm">
                  {e.photoAsset
                    ? <AssetImg className="expert-photo__img" url={e.photoUrl} assetId={e.photoAsset} fallbackPath={photoPath(e.photoAsset)} alt={e.name} />
                    : <Icon name="users" size={16} />}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <strong style={{ fontSize: 14 }}>{e.name}</strong>
                  {e.state !== 'approved' && (
                    <Badge variant={e.state === 'awaiting_review' ? 'warning' : 'neutral'}>
                      {e.state === 'awaiting_review' ? 'Awaiting proofread' : 'Draft'}
                    </Badge>
                  )}
                  {e.title && <span className="dash-muted" style={{ fontSize: 13 }}> — {e.title}</span>}
                  {e.years !== null && <span className="dash-muted" style={{ fontSize: 13 }}> · {e.years} yrs</span>}
                  {e.credentials.length > 0 && <div className="dash-muted" style={{ fontSize: 12 }}>{e.credentials.join(' · ')}</div>}
                  {e.does && <div style={{ fontSize: 13, marginTop: 2 }}>{e.does}</div>}
                  {/* The author-page segment, fixed once set: it becomes a public
                      URL, and renaming it breaks every link to the person. */}
                  {e.slug && <div className="dash-muted" style={{ fontSize: 12, marginTop: 2 }}>/{e.slug}</div>}
                  {e.authorPagePushedAt && (
                    <div className="dash-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      Author page pushed {pushedOn(e.authorPagePushedAt)}
                      {e.authorPageUrl && <> · <a href={e.authorPageUrl} target="_blank" rel="noopener noreferrer">view in repo</a></>}
                    </div>
                  )}
                  {pushed?.id === e.id && (
                    <div className="expert-note"><Icon name="check" size={12} /> Pushed to {pushed.route} — live after the site rebuilds.</div>
                  )}
                  {confirm?.id === e.id && (
                    <div className="expert-confirm">
                      <p>{confirm.message}</p>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button variant="danger" size="sm" disabled={busy} onClick={() => push.mutate({ id: e.id, replace: true })}>Replace page</Button>
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                  {e.socials.length > 0 && (
                    <div className="expert-social-links">
                      {e.socials.map((s) => (
                        <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" title={socialMeta(s.platform).label}>
                          <Icon name={socialMeta(s.platform).icon} size={14} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button
                    variant="outline" size="sm" iconLeft={<Icon name="upload" size={14} />}
                    disabled={busy || !canPush}
                    title={canPush ? `Write ${e.name}'s author page to the site repo` : "Set this client's Astro repo in Setup first"}
                    onClick={() => push.mutate({ id: e.id, replace: false })}
                  >
                    {push.isPending && push.variables?.id === e.id ? 'Pushing…' : e.authorPagePushedAt ? 'Update page' : 'Push page'}
                  </Button>
                  {/* Approving is a decision about a record, not an edit of it —
                      hence its own action rather than a field on the form. */}
                  {e.state !== 'approved' && (
                    <Button variant="primary" size="sm" disabled={busy} onClick={() => stateM.mutate({ id: e.id, state: 'approved' })}>
                      Approve
                    </Button>
                  )}
                  {e.state === 'approved' && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => stateM.mutate({ id: e.id, state: 'awaiting_review' })}>
                      Reopen
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setDraft(toDraft(e)); setEditing(e.id); setAdding(false); }}>Edit</Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => archive.mutate(e.id)}>Archive</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
